import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import {
  Check, X, Leaf, MapPin, Phone, Mail, CheckCircle, AlertCircle, Sparkles, Repeat, Loader, FileText,
  ClipboardList, Info, ChevronDown, ChevronUp, XCircle
} from 'lucide-react';
import { formatCurrency, formatDate } from '../utils/helpers';
import { cleaningFrequencies, standardServiceScope, getScopeTypeForFrequency } from '../utils/estimateData';
import './ClientPortal.css';

const frequencyDescriptions = {
  weekly: 'Best value for busy households',
  biweekly: 'Most popular choice',
  monthly: 'Great for light maintenance',
  one_time: 'Single deep cleaning service'
};

function ClientPortal() {
  const { token } = useParams();
  const { approveEstimate } = useApp();
  
  const [estimate, setEstimate] = useState(null);
  const [client, setClient] = useState(null);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) { setLoading(false); return; }
      try {
        const estimatesRef = collection(db, 'estimates');
        const q = query(estimatesRef, where('approvalToken', '==', token));
        const querySnapshot = await getDocs(q);
        if (querySnapshot.empty) { setLoading(false); return; }
        const estimateDoc = querySnapshot.docs[0];
        const estimateData = { id: estimateDoc.id, ...estimateDoc.data() };
        setEstimate(estimateData);
        if (estimateData.clientId) {
          const clientDoc = await getDoc(doc(db, 'clients', estimateData.clientId));
          if (clientDoc.exists()) setClient({ id: clientDoc.id, ...clientDoc.data() });
        }
        if (estimateData.propertyId) {
          const propertyDoc = await getDoc(doc(db, 'properties', estimateData.propertyId));
          if (propertyDoc.exists()) setProperty({ id: propertyDoc.id, ...propertyDoc.data() });
        }
      } catch (error) {
        console.error('Error fetching estimate data:', error);
        setFetchError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);
  
  const isCustomEstimate = estimate?.source === 'custom_estimate_builder' || 
    estimate?.serviceType === 'commercial' || estimate?.serviceType === 'hoarding' ||
    estimate?.serviceType === 'junk_removal' || estimate?.serviceType === 'custom' ||
    (estimate?.lineItems?.length > 0 && !estimate?.calculatorInputs?.sqft);

  const ONE_TIME_FREQUENCIES = ['one_time', 'deep_clean', 'move_in_out', 'move_in', 'move_out'];
  const estimateFreq = estimate?.calculatorInputs?.frequency || estimate?.frequency;
  const isOneTimeService = !isCustomEstimate &&
    (ONE_TIME_FREQUENCIES.includes(estimateFreq) ||
     estimate?.skipRecurring === true ||
     (estimate?.selectedFrequencies?.length > 0 && estimate.selectedFrequencies.every(f => ONE_TIME_FREQUENCIES.includes(f))));

  const frequencyOptions = useMemo(() => {
    if (!estimate || isCustomEstimate || isOneTimeService) return [];
    if (estimate.frequencyOptions && estimate.frequencyOptions.length > 0) {
      // Filter out deep_clean/one_time - those are initial visit types, not recurring schedules
      return estimate.frequencyOptions
        .filter(opt => !['deep_clean', 'one_time', 'move_in', 'move_out', 'move_in_out'].includes(opt.value))
        .map(opt => ({
          ...opt, 
          label: cleaningFrequencies[opt.value]?.label || opt.label || opt.value,
          description: frequencyDescriptions[opt.value] || ''
        }));
    }
    const freq = estimate.calculatorInputs?.frequency;
    if (!freq || freq === 'one_time' || freq === 'deep_clean') return [];
    return [{
      value: freq,
      label: cleaningFrequencies[freq]?.label || freq,
      price: estimate.recurringTotal || 0,
      description: frequencyDescriptions[freq] || ''
    }];
  }, [estimate, isCustomEstimate, isOneTimeService]);

  const [selectedFrequency, setSelectedFrequency] = useState('');
  const [selectedAddons, setSelectedAddons] = useState({});
  const [selectedLineItems, setSelectedLineItems] = useState({});
  const [clientNotes, setClientNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [isDeclined, setIsDeclined] = useState(false);

  useEffect(() => {
    if (frequencyOptions.length > 0 && !selectedFrequency) {
      const biweekly = frequencyOptions.find(o => o.value === 'biweekly');
      setSelectedFrequency(biweekly?.value || frequencyOptions[0]?.value);
    }
  }, [frequencyOptions, selectedFrequency]);
  
  useEffect(() => {
    const initial = {};
    // Check deepCleanLineItems for addons
    if (estimate?.deepCleanLineItems) {
      estimate.deepCleanLineItems.forEach(item => {
        if (item.type === 'addon') initial[item.id] = item.selected !== false;
      });
    }
    // Also check lineItems for addons (one-time services)
    if (estimate?.lineItems) {
      estimate.lineItems.forEach(item => {
        if (item.type === 'addon' && !(item.id in initial)) {
          initial[item.id] = item.selected !== false;
        }
      });
    }
    if (Object.keys(initial).length > 0) setSelectedAddons(initial);
  }, [estimate]);

  useEffect(() => {
    if (isCustomEstimate && estimate?.lineItems) {
      const initial = {};
      estimate.lineItems.forEach((item, index) => {
        const itemId = item.id || index;
        initial[itemId] = item.required !== false ? true : (item.selected !== false);
      });
      setSelectedLineItems(initial);
    }
  }, [estimate, isCustomEstimate]);

  const getFrequencyPrice = (freq) => {
    const option = frequencyOptions.find(o => o.value === freq);
    return option?.price || estimate?.recurringTotal || 0;
  };

  const totals = useMemo(() => {
    if (!estimate) return { recurring: 0, deepClean: 0, deepCleanBase: 0, customTotal: 0 };
    
    if (isCustomEstimate) {
      let requiredTotal = 0, optionalTotal = 0;
      estimate.lineItems?.forEach((item, index) => {
        const itemId = item.id || index;
        const itemQty = item.qty ?? item.quantity ?? 1;
        const itemTotal = itemQty * (item.unitPrice || item.price || 0);
        if (item.required !== false) requiredTotal += itemTotal;
        else if (selectedLineItems[itemId]) optionalTotal += itemTotal;
      });
      const subtotal = requiredTotal + optionalTotal;
      let discount = 0;
      if (estimate.discountType === 'percent' && estimate.discountValue) discount = subtotal * (estimate.discountValue / 100);
      else if (estimate.discountType === 'fixed' && estimate.discountValue) discount = estimate.discountValue;
      else if (estimate.discountAmount) discount = estimate.discountAmount;
      return { recurring: 0, deepClean: 0, deepCleanBase: 0, customRequired: requiredTotal, customOptional: optionalTotal, customDiscount: discount, customTotal: subtotal - discount };
    }
    
    if (isOneTimeService) {
      let deepCleanBase = estimate.deepCleanTotal || estimate.recurringTotal || estimate.total || 0;
      let deepCleanAddons = 0;
      // Check deepCleanLineItems first, then lineItems for addons
      const dcItems = estimate.deepCleanLineItems && estimate.deepCleanLineItems.length > 0
        ? estimate.deepCleanLineItems
        : estimate.lineItems || [];
      if (estimate.priceOverrides?.deepClean != null) {
        // Use overridden price directly, only sum addons
        dcItems.forEach(item => {
          if (item.type === 'addon' && selectedAddons[item.id]) deepCleanAddons += (item.price || 0);
        });
      } else if (dcItems.length > 0) {
        let baseFromItems = 0;
        dcItems.forEach(item => {
          if (item.type === 'addon') {
            if (selectedAddons[item.id]) deepCleanAddons += (item.price || 0);
          } else {
            baseFromItems += (item.price || 0);
          }
        });
        if (baseFromItems > 0) deepCleanBase = baseFromItems;
      }
      return { recurring: 0, deepClean: deepCleanBase + deepCleanAddons, deepCleanBase, deepCleanAddons };
    }
    
    const recurringTotal = getFrequencyPrice(selectedFrequency);
    if (estimate.skipDeepClean) return { recurring: recurringTotal, deepClean: 0, deepCleanBase: 0, deepCleanAddons: 0 };

    let deepCleanBase = 0, deepCleanAddons = 0;
    const dcItems = (estimate.deepCleanLineItems && estimate.deepCleanLineItems.length > 0)
      ? estimate.deepCleanLineItems
      : (estimate.lineItems || []);

    // If deep clean price was overridden, use the stored deepCleanTotal directly
    if (estimate.priceOverrides?.deepClean != null) {
      deepCleanBase = estimate.deepCleanTotal || 0;
    } else if (dcItems.some(item => item.type === 'addon' || item.type === 'base')) {
      dcItems.forEach(item => {
        if (item.type === 'addon') { if (selectedAddons[item.id]) deepCleanAddons += (item.price || 0); }
        else deepCleanBase += (item.price || 0);
      });
    }
    if (deepCleanBase === 0) deepCleanBase = estimate.deepCleanTotal || 0;

    // Separate addon calculation when override is used (addons still selectable)
    if (estimate.priceOverrides?.deepClean != null && dcItems.length > 0) {
      dcItems.forEach(item => {
        if (item.type === 'addon' && selectedAddons[item.id]) deepCleanAddons += (item.price || 0);
      });
    }

    return { recurring: recurringTotal, deepClean: deepCleanBase + deepCleanAddons, deepCleanBase, deepCleanAddons };
  }, [estimate, selectedAddons, selectedFrequency, frequencyOptions, isCustomEstimate, isOneTimeService, selectedLineItems]);

  const frequencyConfig = cleaningFrequencies[selectedFrequency];
  const isExpired = estimate?.validUntil && new Date(estimate.validUntil) < new Date();
  const isAlreadyResponded = estimate?.status === 'approved' || estimate?.status === 'declined';

  const toggleAddon = (itemId) => setSelectedAddons(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  
  const toggleLineItem = (itemId) => {
    const item = estimate?.lineItems?.find((i, idx) => (i.id || idx) === itemId);
    if (item?.required !== false) return;
    setSelectedLineItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      if (isCustomEstimate) {
        const approvedItems = estimate.lineItems?.map((item, index) => ({
          ...item, approvedByClient: item.required !== false ? true : selectedLineItems[item.id || index]
        }));
        await approveEstimate(estimate.id, { approvedLineItems: approvedItems, approvedTotal: totals.customTotal, isCustomEstimate: true, clientNotes });
      } else if (isOneTimeService) {
        const approvedDeepCleanItems = estimate.deepCleanLineItems?.map(item => ({
          ...item, approvedByClient: item.type === 'addon' ? selectedAddons[item.id] : true
        }));
        await approveEstimate(estimate.id, { approvedDeepCleanTotal: totals.deepClean, approvedDeepCleanItems, isOneTimeService: true, clientNotes });
      } else {
        const approvedDeepCleanItems = estimate.deepCleanLineItems?.map(item => ({
          ...item, approvedByClient: item.type === 'addon' ? selectedAddons[item.id] : true
        }));
        await approveEstimate(estimate.id, { approvedLineItems: estimate.lineItems, approvedDeepCleanItems, approvedRecurringTotal: totals.recurring, approvedDeepCleanTotal: totals.deepClean, approvedFrequency: selectedFrequency, clientNotes });
      }
      setIsApproved(true);
    } catch (error) {
      console.error('Error approving estimate:', error);
      alert('Failed to approve estimate. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = async () => {
    try {
      const estimateRef = doc(db, 'estimates', estimate.id);
      await updateDoc(estimateRef, { status: 'declined', declinedAt: new Date().toISOString(), declineReason: clientNotes || 'No reason provided', updatedAt: new Date().toISOString() });
      setIsDeclined(true);
    } catch (error) {
      console.error('Error declining estimate:', error);
      alert('Failed to decline estimate. Please try again.');
    }
  };

  const [scopeModalOpen, setScopeModalOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Determine the scope type for this estimate
  const scopeType = useMemo(() => {
    if (isCustomEstimate) return null; // Custom estimates don't have standard scope
    // Use selected frequency first (user's current choice), then calculator inputs, then estimate-level frequency
    const freq = selectedFrequency || estimate?.calculatorInputs?.frequency || estimate?.frequency;
    if (isOneTimeService) return getScopeTypeForFrequency(estimateFreq) || 'deep_clean';
    if (!freq) return 'recurring';
    return getScopeTypeForFrequency(freq);
  }, [estimate, isCustomEstimate, isOneTimeService, selectedFrequency]);

  const scopeData = scopeType ? standardServiceScope[scopeType] : null;

  // Check if the estimate has any variance notes from the admin
  const hasVarianceNotes = estimate?.scopeNotes || estimate?.notes;

  const PortalHeader = () => (
    <div className="portal-header">
      <div className="logo">
        <div className="logo-icon"><Leaf size={24} /></div>
        <div><h1>Two Sisters</h1><span>Cleaning & Organizing</span></div>
      </div>
    </div>
  );

  // Scope Details Modal
  const ScopeModal = () => {
    if (!scopeModalOpen || !scopeData) return null;
    return (
      <div className="scope-modal-overlay" onClick={() => setScopeModalOpen(false)}>
        <div className="scope-modal" onClick={(e) => e.stopPropagation()}>
          <div className="scope-modal-header">
            <div>
              <h3><ClipboardList size={20} /> Standard Scope of Service</h3>
              <span className="scope-modal-subtitle">{scopeData.label} — {scopeData.subtitle}</span>
            </div>
            <button className="scope-modal-close" onClick={() => setScopeModalOpen(false)}><X size={20} /></button>
          </div>

          <div className="scope-modal-body">
            <div className="scope-variance-banner">
              <Info size={16} />
              <div>
                <strong>Every home is unique.</strong> The items below represent our standard scope for this service type. Your specific estimate may include additions or exclusions based on your home's needs or your requests — any such variances are detailed on your quote above.
              </div>
            </div>

            {Object.entries(scopeData.rooms).map(([key, room]) => (
              <div key={key} className="scope-room">
                <h4 className="scope-room-title"><span className="scope-room-icon">{room.icon}</span> {room.label}</h4>
                <ul className="scope-item-list">
                  {room.items.map((item, idx) => {
                    const text = typeof item === 'string' ? item : item.text;
                    const warning = typeof item === 'string' ? null : item.warning;
                    const isHighlight = text.includes('PLUS:');
                    return (
                      <li key={idx} className={isHighlight ? 'scope-item-highlight' : ''}>
                        {!isHighlight && <Check size={14} className="scope-check-icon" />}
                        <span>
                          {text}
                          {warning && (
                            <span className="scope-item-warning">
                              <AlertCircle size={12} /> {warning}
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}

            {scopeData.notIncluded && scopeData.notIncluded.length > 0 && (
              <div className="scope-not-included">
                <h4 className="scope-not-included-title"><XCircle size={16} /> Not Included in Standard Scope</h4>
                <ul className="scope-not-included-list">
                  {scopeData.notIncluded.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="scope-modal-footer-note">
              <Info size={14} />
              <span>Questions about what's included? Contact us anytime — we're happy to customize your service.</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const deepCleanAddonsItems = estimate?.deepCleanLineItems?.filter(item => item.type === 'addon') || [];
  // Also check lineItems for addons (one-time services store addons there)
  const lineItemAddons = estimate?.lineItems?.filter(item => item.type === 'addon') || [];
  // Use deepCleanLineItems addons first, fall back to lineItems addons
  const allAddonsItems = deepCleanAddonsItems.length > 0 ? deepCleanAddonsItems : lineItemAddons;
  const hasAddons = allAddonsItems.length > 0;
  const hasDeepClean = !isOneTimeService && !estimate?.skipDeepClean && (estimate?.deepCleanTotal > 0 || totals.deepCleanBase > 0);
  const requiredLineItems = estimate?.lineItems?.filter(item => item.required !== false) || [];
  const optionalLineItems = estimate?.lineItems?.filter(item => item.required === false) || [];

  // Loading state
  if (loading) {
    return (
      <div className="client-portal"><div className="portal-container"><div className="portal-card">
        <PortalHeader />
        <div className="portal-content"><div className="portal-loading">
          <Loader size={48} className="spinning" /><h2>Loading your estimate...</h2><p>Please wait while we fetch your details.</p>
        </div></div>
      </div></div></div>
    );
  }

  // Invalid token
  if (!estimate) {
    return (
      <div className="client-portal"><div className="portal-container"><div className="portal-card">
        <PortalHeader />
        <div className="portal-content"><div className="portal-error">
          <AlertCircle size={48} /><h2>Invalid Link</h2>
          <p>This estimate link is invalid or has been removed. Please contact Two Sisters Cleaning for a new estimate.</p>
          <a href="tel:+14342072246" className="btn btn-primary"><Phone size={18} /> Call Us</a>
        </div></div>
      </div></div></div>
    );
  }

  // Already responded
  if (isAlreadyResponded && !isApproved && !isDeclined) {
    return (
      <div className="client-portal"><div className="portal-container"><div className="portal-card">
        <PortalHeader />
        <div className="portal-content"><div className="portal-message">
          <CheckCircle size={48} /><h2>Already Responded</h2>
          <p>You've already {estimate.status === 'approved' ? 'approved' : 'declined'} this estimate on {formatDate(estimate.approvedAt || estimate.declinedAt)}.</p>
          {estimate.status === 'approved' && <p className="message-note">We'll be in touch soon to schedule your first cleaning!</p>}
        </div></div>
      </div></div></div>
    );
  }

  // Expired
  if (isExpired && !isApproved) {
    return (
      <div className="client-portal"><div className="portal-container"><div className="portal-card">
        <PortalHeader />
        <div className="portal-content"><div className="portal-error">
          <AlertCircle size={48} /><h2>Estimate Expired</h2>
          <p>This estimate expired on {formatDate(estimate.validUntil)}. Don't worry — we can renew it! Give us a call or send a text and we'll get you a fresh link.</p>
          <a href="tel:+14342072246" className="btn btn-primary"><Phone size={18} /> Call Us to Renew</a>
        </div></div>
      </div></div></div>
    );
  }

  // Approved state
  if (isApproved) {
    return (
      <div className="client-portal"><div className="portal-container"><div className="portal-card">
        <PortalHeader />
        <div className="portal-content"><div className="portal-success">
          <CheckCircle size={64} /><h2>You're All Set! 🎉</h2>
          <p>Thank you for choosing Two Sisters Cleaning. We'll contact you shortly to schedule your service.</p>
          <div className="success-summary">
            {isCustomEstimate ? (
              <div className="summary-row highlight"><span>{estimate?.title || 'Custom Quote'}</span><span>{formatCurrency(totals.customTotal)}</span></div>
            ) : isOneTimeService ? (
              <div className="summary-row highlight"><span>Deep Clean Service</span><span>{formatCurrency(totals.deepClean)}</span></div>
            ) : (
              <>
                {totals.deepClean > 0 && <div className="summary-row"><span>First Visit (Deep Clean)</span><span>{formatCurrency(totals.deepClean)}</span></div>}
                <div className="summary-row highlight"><span>{frequencyConfig?.label} Cleaning</span><span>{formatCurrency(totals.recurring)} / visit</span></div>
              </>
            )}
          </div>
          <div className="contact-info">
            <p>Questions? We're here to help!</p>
            <a href="tel:+14342072246"><Phone size={16} /> (434) 207-2246</a>
            <a href="mailto:hello@twosisterscleaningco.com"><Mail size={16} /> hello@twosisterscleaningco.com</a>
          </div>
        </div></div>
      </div></div></div>
    );
  }

  // Declined state
  if (isDeclined) {
    return (
      <div className="client-portal"><div className="portal-container"><div className="portal-card">
        <PortalHeader />
        <div className="portal-content"><div className="portal-declined">
          <h2>Estimate Declined</h2>
          <p>We're sorry to hear you've decided not to proceed. If you change your mind or would like to discuss different options, please don't hesitate to reach out.</p>
          <div className="contact-info">
            <a href="tel:+14342072246"><Phone size={16} /> (434) 207-2246</a>
            <a href="mailto:hello@twosisterscleaningco.com"><Mail size={16} /> hello@twosisterscleaningco.com</a>
          </div>
        </div></div>
      </div></div></div>
    );
  }

  // Main estimate view
  return (
    <div className="client-portal">
      <ScopeModal />
      <div className="portal-container">
        <div className="portal-card">
          <PortalHeader />
          <div className="portal-content">
            {/* Welcome */}
            <div className="portal-welcome">
              <span className="welcome-emoji">👋</span>
              <h2>Hello{client?.firstName ? `, ${client.firstName}` : ''}!</h2>
              <p>{isCustomEstimate ? 'Review your custom quote below and approve when ready.' : 'Customize your cleaning service below and approve when ready.'}</p>
            </div>

            {/* Variance Disclaimer + Scope Link */}
            {!isCustomEstimate && scopeData && (
              <div className="scope-disclaimer-section">
                <div className="scope-disclaimer">
                  <Info size={16} className="scope-disclaimer-icon" />
                  <div>
                    <p className="scope-disclaimer-text">
                      <strong>Every estimate can vary</strong> based on the unique needs of your home.
                      The quote below reflects your personalized pricing. Any items that differ from our{' '}
                      <button className="scope-link-inline" onClick={() => setScopeModalOpen(true)}>
                        standard scope of service
                      </button>{' '}
                      — whether included or excluded at your request — are detailed in your quote.
                    </p>
                  </div>
                </div>
                <button className="scope-link-button" onClick={() => setScopeModalOpen(true)}>
                  <ClipboardList size={16} />
                  <span>View Standard Scope for {scopeData.label}</span>
                  <ChevronDown size={14} />
                </button>
              </div>
            )}

            {/* Property Info */}
            <div className="portal-section">
              <h3><MapPin size={18} /> Service Location</h3>
              <div className="property-card">
                {property ? (
                  <><p className="property-address">{property.addressLine1 || property.street}</p><p className="property-city">{property.city}, {property.state} {property.zip || property.zipCode}</p></>
                ) : estimate?.propertyAddress ? (
                  <p className="property-address">{estimate.propertyAddress}</p>
                ) : estimate?.propertyInfo?.street ? (
                  <><p className="property-address">{estimate.propertyInfo.street}</p><p className="property-city">{estimate.propertyInfo.city}, {estimate.propertyInfo.state} {estimate.propertyInfo.zip}</p></>
                ) : (
                  <p className="property-address">Address to be confirmed</p>
                )}
              </div>
            </div>

            {/* RESIDENTIAL: Frequency Selection */}
            {frequencyOptions.length > 0 && !isCustomEstimate && !isOneTimeService && (
              <div className="portal-section">
                <h3><Repeat size={18} /> Choose Your Schedule</h3>
                <p className="section-note">How often would you like us to clean?</p>
                <div className="frequency-options">
                  {frequencyOptions.map(option => (
                    <div key={option.value} className={`frequency-option ${selectedFrequency === option.value ? 'selected' : ''}`} onClick={() => setSelectedFrequency(option.value)}>
                      <div className="frequency-radio">{selectedFrequency === option.value && <Check size={14} />}</div>
                      <div className="frequency-info">
                        <span className="frequency-label">{option.label}</span>
                        {option.description && <span className="frequency-desc">{option.description}</span>}
                        {estimate?.frequencyNotes?.[option.value] && (
                          <span className="frequency-note">{estimate.frequencyNotes[option.value]}</span>
                        )}
                      </div>
                      <div className="frequency-price">{formatCurrency(option.price)}<span className="per-visit">/ visit</span></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* RESIDENTIAL: What's Included (recurring line items with descriptions) */}
            {!isCustomEstimate && !isOneTimeService && estimate?.lineItems?.some(item => item.clientDescription) && (
              <div className="portal-section">
                <h3>✨ What's Included</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {estimate.lineItems.filter(item => item.clientDescription).map((item, idx) => (
                    <div key={idx} style={{ background: '#f8faf9', borderRadius: '10px', padding: '14px 16px', border: '1px solid #e2e8e4' }}>
                      <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '4px' }}>{item.label}</div>
                      <div style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: '1.5' }}>{item.clientDescription}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ONE-TIME: Deep Clean Service Display */}
            {isOneTimeService && (
              <div className="portal-section">
                <h3><Sparkles size={18} /> Your Service</h3>
                <div className="one-time-service">
                  <div className="one-time-header"><span className="one-time-badge"><Check size={12} /> Selected</span></div>
                  <p className="one-time-name">{cleaningFrequencies[estimateFreq]?.label || ({ move_in: 'Move In', move_out: 'Move Out', deep_clean: 'Deep Clean' })[estimateFreq] || 'Deep Clean Service'}</p>
                  {/* Show base line item descriptions for one-time */}
                  {estimate?.deepCleanLineItems?.filter(item => item.type === 'base' && item.clientDescription).map((item, idx) => (
                    <p key={idx} className="service-description">{item.clientDescription}</p>
                  ))}
                  {estimate?.frequencyNotes?.[estimateFreq] && (
                    <p className="service-note">{estimate.frequencyNotes[estimateFreq]}</p>
                  )}
                  <p className="one-time-price">{formatCurrency(totals.deepCleanBase)}</p>
                </div>

                {/* Add-ons for one-time service */}
                {allAddonsItems.length > 0 && (
                  <div className="addons-container" style={{ marginTop: '16px' }}>
                    <div className="addons-title">Optional Add-Ons</div>
                    {allAddonsItems.map(item => (
                      <div key={item.id} className={`addon-item ${selectedAddons[item.id] ? 'selected' : ''}`} onClick={() => toggleAddon(item.id)}>
                        <div className={`addon-checkbox ${selectedAddons[item.id] ? 'checked' : ''}`}>{selectedAddons[item.id] && <Check size={14} />}</div>
                        <div className="addon-info">
                          <span className="addon-name">{item.label}</span>
                          {(item.clientDescription || item.description) && <span className="addon-desc">{item.clientDescription || item.description}</span>}
                        </div>
                        <span className="addon-price">+{formatCurrency(item.price)}</span>
                      </div>
                    ))}
                    {totals.deepCleanAddons > 0 && (
                      <div className="deep-clean-total" style={{ marginTop: '8px' }}><span>Total with Add-Ons</span><span>{formatCurrency(totals.deepClean)}</span></div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* CUSTOM QUOTE: Line Items */}
            {isCustomEstimate && estimate?.lineItems?.length > 0 && (
              <div className="portal-section">
                <h3><FileText size={18} /> {estimate.title || 'Quote Details'}</h3>
                <p className="section-note">Select the services you'd like to include.</p>
                <div className="custom-line-items">
                  {/* Required items */}
                  {requiredLineItems.map((item, index) => {
                    const qty = item.qty ?? item.quantity ?? 1;
                    return (
                    <div key={item.id || index} className="line-item required">
                      <div className="line-item-check included"><Check size={14} /></div>
                      <div className="line-item-info">
                        <span className="line-item-name">{item.description}</span>
                        {qty > 1 && <span className="line-item-desc">{qty} × {formatCurrency(item.unitPrice || item.price)}</span>}
                      </div>
                      <div className="line-item-pricing"><span className="line-item-price">{formatCurrency(qty * (item.unitPrice || item.price || 0))}</span></div>
                    </div>
                    );
                  })}
                  
                  {/* Optional items header */}
                  {optionalLineItems.length > 0 && (
                    <div className="line-items-optional-header"><span>Optional Services</span><span className="optional-hint">Click to add or remove</span></div>
                  )}
                  
                  {/* Optional items */}
                  {optionalLineItems.map((item, index) => {
                    const itemId = item.id || (requiredLineItems.length + index);
                    const isSelected = selectedLineItems[itemId];
                    const qty = item.qty ?? item.quantity ?? 1;
                    return (
                      <div key={itemId} className={`line-item optional ${isSelected ? 'selected' : ''}`} onClick={() => toggleLineItem(itemId)}>
                        <div className="line-item-check">{isSelected && <Check size={14} />}</div>
                        <div className="line-item-info">
                          <span className="line-item-name">{item.description}</span>
                          {qty > 1 && <span className="line-item-desc">{qty} × {formatCurrency(item.unitPrice || item.price)}</span>}
                        </div>
                        <div className="line-item-pricing"><span className="line-item-price">{formatCurrency(qty * (item.unitPrice || item.price || 0))}</span></div>
                      </div>
                    );
                  })}
                  
                  {/* Discount */}
                  {totals.customDiscount > 0 && (
                    <div className="line-item-discount"><span>Discount{estimate.discountReason && ` (${estimate.discountReason})`}</span><span className="discount-amount">-{formatCurrency(totals.customDiscount)}</span></div>
                  )}
                </div>
                {estimate.notes && <div className="estimate-notes"><strong>Notes:</strong><p>{estimate.notes}</p></div>}
              </div>
            )}

            {/* RESIDENTIAL: Deep Clean Section */}
            {hasDeepClean && !isCustomEstimate && (
              <div className="portal-section">
                <h3><Sparkles size={18} /> First Visit - Deep Clean</h3>
                <p className="section-note">Your first visit includes a thorough deep clean to get your home sparkling.</p>
                <div className="deep-clean-section">
                  <div className="deep-clean-header"><Sparkles size={18} /><h4>Deep Clean</h4></div>
               <div className="deep-clean-content">
                    {/* Show deep clean service with total base price (includes adjustments) */}
                    <div className="deep-clean-base">
                      <div>
                        <span>Deep Clean Service</span>
                        {estimate?.deepCleanLineItems?.filter(item => item.type === 'base').map((item, idx) => (
                          item.clientDescription ? <span key={idx} style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', marginTop: '4px' }}>{item.clientDescription}</span> : null
                        ))}
                        {(estimate?.frequencyNotes?.deep_clean || estimate?.frequencyNotes?.one_time) && (
                          <span className="frequency-note" style={{ display: 'block' }}>{estimate.frequencyNotes.deep_clean || estimate.frequencyNotes.one_time}</span>
                        )}
                      </div>
                      <span>{formatCurrency(totals.deepCleanBase)}</span>
                    </div>
                    {allAddonsItems.length > 0 && (
                      <div className="addons-container">
                        <div className="addons-title">Optional Add-Ons</div>
                        {allAddonsItems.map(item => (
                          <div key={item.id} className={`addon-item ${selectedAddons[item.id] ? 'selected' : ''}`} onClick={() => toggleAddon(item.id)}>
                            <div className={`addon-checkbox ${selectedAddons[item.id] ? 'checked' : ''}`}>{selectedAddons[item.id] && <Check size={14} />}</div>
                            <div className="addon-info">
                              <span className="addon-name">{item.label}</span>
                              {(item.clientDescription || item.description) && <span className="addon-desc">{item.clientDescription || item.description}</span>}
                            </div>
                            <span className="addon-price">+{formatCurrency(item.price)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="deep-clean-total"><span>First Visit Total</span><span>{formatCurrency(totals.deepClean)}</span></div>
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            {!isCustomEstimate && estimate?.notes && (
              <div className="portal-section">
                <div className="estimate-notes"><strong>Notes:</strong><p>{estimate.notes}</p></div>
              </div>
            )}

            {/* Order Summary */}
            <div className="order-summary">
              <h4>Your Selection</h4>
              {isCustomEstimate ? (
                <>
                  <div className="summary-line"><span>Required Services</span><span>{formatCurrency(totals.customRequired)}</span></div>
                  {totals.customOptional > 0 && <div className="summary-line"><span>Optional Services</span><span>{formatCurrency(totals.customOptional)}</span></div>}
                  {totals.customDiscount > 0 && <div className="summary-line discount-row"><span>Discount</span><span className="discount-value">-{formatCurrency(totals.customDiscount)}</span></div>}
                  <div className="summary-line highlight total-row"><span>Total</span><span>{formatCurrency(totals.customTotal)}</span></div>
                </>
              ) : isOneTimeService ? (
                <>
                  <div className="summary-line"><span>{cleaningFrequencies[estimateFreq]?.label || ({ move_in: 'Move In', move_out: 'Move Out', deep_clean: 'Deep Clean' })[estimateFreq] || 'Deep Clean'}</span><span>{formatCurrency(totals.deepCleanBase)}</span></div>
                  {totals.deepCleanAddons > 0 && <div className="summary-line"><span>Add-Ons</span><span>{formatCurrency(totals.deepCleanAddons)}</span></div>}
                  <div className="summary-line highlight"><span>Total</span><span>{formatCurrency(totals.deepClean)}</span></div>
                </>
              ) : (
                <>
                  {totals.deepClean > 0 && <div className="summary-line"><span>First Visit (Deep Clean)</span><span>{formatCurrency(totals.deepClean)}</span></div>}
                  <div className="summary-line highlight"><span>{frequencyConfig?.label || 'Recurring'} Cleaning</span><span>{formatCurrency(totals.recurring)}<span className="per-visit"> / visit</span></span></div>
                </>
              )}
            </div>

            {/* Notes */}
            <div className="portal-section notes-section">
              <h3>Questions or Special Requests?</h3>
              <textarea value={clientNotes} onChange={(e) => setClientNotes(e.target.value)} placeholder="Let us know if you have any questions, pet access instructions, or special requests..." rows={3} />
            </div>

            {/* Terms & Conditions Acceptance */}
            <div className="terms-acceptance">
              <label className={`terms-checkbox-label ${termsAccepted ? 'accepted' : ''}`} onClick={() => setTermsAccepted(!termsAccepted)}>
                <div className={`terms-checkbox ${termsAccepted ? 'checked' : ''}`}>
                  {termsAccepted && <Check size={14} />}
                </div>
                <span>
                  I have read and agree to the{' '}
                  <a href="https://keelgroup.app/terms" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                    Terms & Conditions
                  </a>
                </span>
              </label>
            </div>

            {/* Actions */}
            <div className="portal-actions">
              <button className="btn btn-decline" onClick={handleDecline} disabled={isSubmitting}><X size={18} /> Decline</button>
              <button className="btn btn-approve" onClick={handleApprove} disabled={isSubmitting || !termsAccepted}>
                {isSubmitting ? 'Processing...' : <><Check size={18} /> Approve Estimate</>}
              </button>
            </div>

            {/* Contact Footer */}
            <div className="portal-footer">
              <p>Questions about this estimate?</p>
              <div className="footer-contact">
                <a href="tel:+14342072246"><Phone size={16} /> (434) 207-2246</a>
                <a href="mailto:hello@twosisterscleaningco.com"><Mail size={16} /> hello@twosisterscleaningco.com</a>
              </div>
              <a href="https://keelgroup.app/terms" target="_blank" rel="noopener noreferrer" className="footer-terms-link">
                View our Terms & Conditions
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientPortal;
